/**
 * @file MlrDocBlocks.tsx
 * @brief Renders BACKEND's MLR section-model blocks onto the A4 paper.
 *
 * @details
 * The model (`BACKEND export/mlrSectionModel.mjs`) is the one place that
 * decides what belongs in a section; the PDF walks it and draws, this walks it
 * and renders. Before 13-09-2026 the panel assembled the same thirteen
 * sections itself from `mlrDocumentSections.ts` + `mlrEditorCopy.ts`, and the
 * two had already drifted — §7.2 existed only in the PDF, §8.2 showed
 * unconfirmed AI drafts only on screen, and §12.4's statement of truth was a
 * hand-typed second copy of the sentences the doctor signs.
 *
 * Blocks carry an `editable` target naming what they write back to. Rendering
 * uses the block's stripped `items`; the editor opens on `editable.html`, the
 * source with markup intact — opening from the rendered text would strip the
 * doctor's formatting on the next save.
 *
 * @references
 * - BACKEND/src/features/medical-assessment/export/mlrSectionModel.mjs
 * - BACKEND/src/features/medical-assessment/export/mlrPdf.builder.mjs
 *
 * @author DES
 * @date 13-09-2026
 */

import { Fragment } from "react";
import { Check, ShieldAlert, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import type {
  MlrBlock,
  MlrEditableTarget,
  MlrIdentityRow,
  MlrUnverifiedClaim,
} from "../schemas";
import { DocParagraph } from "./DocParagraph";
import {
  MlrIdentityTable,
  MlrNumberLabel,
  MlrStaticParagraph,
  MlrSubheading,
} from "./MlrPrintChrome";

/**
 * `e.1` sits under lettered item `e`, so it indents while its siblings do not.
 * Twin of `isMlrNestedListLine` in BACKEND `export/mlrPdf.theme.mjs` — the one
 * rule still held in two places, because the model marks the indent per BLOCK
 * and the decision is per LINE.
 */
function isMlrNestedListLine(text: string): boolean {
  return /^[a-z]\.\d/i.test(String(text || "").trim());
}

export type MlrBlockBinding = {
  /** Draft value (source html) for an editable target. */
  valueOf: (target: MlrEditableTarget) => string;
  /** Persisted value, for the unsaved-change highlight. */
  originalOf: (target: MlrEditableTarget) => string;
  onChange: (target: MlrEditableTarget, next: string) => void;
  /** Draft value for an identity cell; the row carries the persisted one. */
  identityValue: (row: MlrIdentityRow) => string;
  onIdentityChange: (label: string, next: string) => void;
  onIdentityBlur?: (label: string) => void;
  /**
   * Accept a finding the recording does not support, or remove it. Omit both
   * to render the warning without actions — which is what a signed report
   * wants, since the write would be refused anyway.
   */
  onVerify?: (statementId: string, claim: MlrUnverifiedClaim) => void;
  onRemove?: (statementId: string, claim: MlrUnverifiedClaim) => void;
};

/**
 * A finding the consultation does not support.
 *
 * ⚠️ It is **already withheld** — the PDF and the GRIP export both refuse it
 * (BACKEND `factFlags.mjs`). This does not warn about something about to
 * happen; it explains why a paragraph on screen is not going to print, and
 * offers the only two answers there are.
 *
 * The grounding check is a text match and will sometimes be wrong — a doctor
 * who said "trapezius" while the extractor wrote "shoulders" really did record
 * it. That is why accepting exists, and why nothing clears the flag on its own.
 * The claim is named because "shoulders was never mentioned" is checkable
 * against memory of the room; "unverified content" is not. (17-09-2026)
 */
function UnverifiedNotice({
  claim,
  statementId,
  binding,
}: {
  claim: MlrUnverifiedClaim;
  statementId?: string;
  binding: MlrBlockBinding;
}) {
  const canAct = Boolean(
    statementId && (binding.onVerify || binding.onRemove),
  );

  return (
    <div className="my-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
      <div className="flex items-center gap-1.5 font-semibold text-destructive">
        <ShieldAlert className="h-4 w-4" />
        Not in the recording
      </div>
      <p className="mt-1 text-muted-foreground">
        Nothing in the consultation mentions{" "}
        <span className="font-medium text-foreground">{claim.value}</span>. This
        paragraph will not print, and is left out of the MLR and the GRIP
        export, until you say otherwise.
      </p>
      {canAct ? (
        <div className="mt-2 flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => binding.onVerify?.(statementId as string, claim)}
          >
            <Check className="mr-1.5 h-3.5 w-3.5 text-emerald-600" />
            Yes, I recorded this
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="text-destructive hover:text-destructive"
            onClick={() => binding.onRemove?.(statementId as string, claim)}
          >
            <Trash2 className="mr-1.5 h-3.5 w-3.5" />
            Remove
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function editableLabel(target: MlrEditableTarget): string {
  switch (target.kind) {
    case "statement":
      return `${target.xmlTag} narrative`;
    case "examination":
      return target.field === "generalFindings"
        ? "General examination"
        : "Client presentation";
    case "examinationInjury":
      return `Examination ${target.injuryKey.replace(/_/g, " ")}`;
    case "supplements":
      return target.field;
    default:
      return "Report text";
  }
}

function richTextDiffers(a: string, b: string): boolean {
  const norm = (value: string) =>
    String(value ?? "")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  return norm(a) !== norm(b);
}

function EditableParagraph({
  target,
  binding,
  placeholderText,
  minRows,
}: {
  target: MlrEditableTarget;
  binding: MlrBlockBinding;
  placeholderText?: string;
  minRows?: number;
}) {
  const value = binding.valueOf(target);
  return (
    <DocParagraph
      value={value}
      onChange={(next) => binding.onChange(target, next)}
      ariaLabel={editableLabel(target)}
      placeholder={placeholderText}
      dirty={richTextDiffers(value, binding.originalOf(target))}
      minRows={minRows}
    />
  );
}

function IdentityBlock({
  rows,
  binding,
}: {
  rows: MlrIdentityRow[];
  binding: MlrBlockBinding;
}) {
  return (
    <MlrIdentityTable
      rows={rows.map((row) => ({
        label: row.label,
        value: binding.identityValue(row),
        // The model says how each cell is edited, beside the label list it
        // came from — the panel no longer keeps its own multiline set.
        multiline: row.kind === "multiline",
        onChange: (next: string) => binding.onIdentityChange(row.label, next),
        onBlur: binding.onIdentityBlur
          ? () => binding.onIdentityBlur?.(row.label)
          : undefined,
      }))}
    />
  );
}

export function MlrDocBlock({
  block,
  binding,
}: {
  block: MlrBlock;
  binding: MlrBlockBinding;
}) {
  switch (block.type) {
    case "identityTable":
      return <IdentityBlock rows={block.rows} binding={binding} />;

    case "subheading":
      return <MlrSubheading>{block.text}</MlrSubheading>;

    case "numberLabel":
      return <MlrNumberLabel>{block.text}</MlrNumberLabel>;

    case "numberedHeading":
      return (
        <MlrSubheading>{`${block.number}  ${block.title}`}</MlrSubheading>
      );

    case "paragraphs": {
      /* Shown UNDER the words, never instead of them: a person has to read
         what was proposed in order to judge it. */
      const notice = block.unverified ? (
        <UnverifiedNotice
          claim={block.unverified}
          statementId={
            block.editable && "id" in block.editable
              ? block.editable.id
              : undefined
          }
          binding={binding}
        />
      ) : null;

      if (!block.editable) {
        return (
          <>
            {block.items.map((item, index) => (
              <MlrStaticParagraph
                key={`${index}-${item.slice(0, 24)}`}
                nested={Boolean(block.nestedListIndent) && isMlrNestedListLine(item)}
              >
                {item}
              </MlrStaticParagraph>
            ))}
            {notice}
          </>
        );
      }
      return (
        <>
          <EditableParagraph
            target={block.editable}
            binding={binding}
            // A placeholder block's text is a "nothing recorded yet" line, not
            // content: it prefills the greyed hint, never the editor.
            placeholderText={
              block.placeholder ? block.items.join("\n") : undefined
            }
            minRows={block.items.length > 2 ? 4 : 2}
          />
          {notice}
        </>
      );
    }

    // `records` and `declaration` carry payloads for purpose-built renderers
    // rather than block-shaped content; the document handles those sections
    // itself and never reaches this branch.
    default:
      return null;
  }
}

export function MlrDocBlocks({
  blocks,
  binding,
  skip,
}: {
  blocks: MlrBlock[];
  binding: MlrBlockBinding;
  /** Block types this section renders itself (e.g. `records`). */
  skip?: ReadonlyArray<MlrBlock["type"]>;
}) {
  return (
    <>
      {blocks.map((block, index) =>
        skip?.includes(block.type) ? null : (
          <Fragment key={`${block.type}-${index}`}>
            <MlrDocBlock block={block} binding={binding} />
          </Fragment>
        ),
      )}
    </>
  );
}
